import { UserAgent } from '@hooks/useUserAgent';
import { Bech32Address } from '@keplr-wallet/cosmos';
import {
  EthChainId,
  Network,
  WalletType,
  CroChain,
  CronosChain,
  Networks,
  NetworkChainExplorer,
  CosmosHubChain,
  TerraChain,
  JunoChain,
  AkashChain,
  CantoChain,
  IrisChain,
  CelestiaChain,
} from '@types';

const infuraKey = process.env.NEXT_PUBLIC_INFURAKEY;

const baseStaticConfig = {
  cosmos: {
    cosmosHub: {
      chainInfo: {
        mainnet: {
          bridgeRpcs: [
            'https://cosmoshub-rpc.crypto.org',
            'https://cosmoshub.validator.network',
          ],
          rpc: 'https://cosmoshub-rpc.crypto.org', // local use https://cosmoshub.validator.network
          rest: 'https://lcd-cosmoshub.keplr.app',
          chainId: 'cosmoshub-4',
          chainName: 'Cosmos',
          stakeCurrency: {
            coinDenom: 'ATOM',
            coinMinimalDenom: 'uatom',
            coinDecimals: 6,
            coinGeckoId: 'cosmos',
          },
          walletUrl: 'https://wallet.keplr.app/#/cosmoshub/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/cosmoshub/stake',
          bip44: {
            coinType: 118,
          },
          bech32Config: Bech32Address.defaultBech32Config('cosmos'),
          currencies: [
            {
              coinDenom: 'ATOM',
              coinMinimalDenom: 'uatom',
              coinDecimals: 6,
              coinGeckoId: 'cosmos',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/atom.png',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'ATOM',
              coinMinimalDenom: 'uatom',
              coinDecimals: 6,
              coinGeckoId: 'cosmos',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/atom.png',
            },
          ],
          coinType: 118,
          features: ['stargate', 'ibc-transfer'],
        },
        testnet: {
          rpc: 'https://cosmoshub-rpc.crypto.org',
          rest: 'https://lcd-cosmoshub.keplr.app',
          chainId: 'cosmoshub-4',
          chainName: 'Cosmos',
          stakeCurrency: {
            coinDenom: 'ATOM',
            coinMinimalDenom: 'uatom',
            coinDecimals: 6,
            coinGeckoId: 'cosmos',
          },
          walletUrl: 'https://wallet.keplr.app/#/cosmoshub/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/cosmoshub/stake',
          bip44: {
            coinType: 118,
          },
          bech32Config: Bech32Address.defaultBech32Config('cosmos'),
          currencies: [
            {
              coinDenom: 'ATOM',
              coinMinimalDenom: 'uatom',
              coinDecimals: 6,
              coinGeckoId: 'cosmos',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/atom.png',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'ATOM',
              coinMinimalDenom: 'uatom',
              coinDecimals: 6,
              coinGeckoId: 'cosmos',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/atom.png',
            },
          ],
          coinType: 118,
          features: ['stargate', 'ibc-transfer'],
        },
      },
    },
    cryptoOrg: {
      chainInfo: {
        mainnet: {
          rpc: 'https://rpc.mainnet.crypto.org/',
          rpcConfig: undefined,
          rest: 'https://mainnet.crypto.org:1317',
          restConfig: undefined,
          chainId: 'crypto-org-chain-mainnet-1',
          chainName: 'Crypto.org Mainnet',
          stakeCurrency: {
            coinDenom: 'cro',
            coinMinimalDenom: 'basecro',
            coinDecimals: 8,
            coinGeckoId: 'crypto-com-chain',
          },
          walletUrl: 'https://wallet.keplr.app/#/crypto-org/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/crypto-org/stake',
          bip44: {
            coinType: 394,
          },
          bech32Config: Bech32Address.defaultBech32Config('cro'),
          currencies: [
            {
              coinDenom: 'cro',
              coinMinimalDenom: 'basecro',
              coinDecimals: 8,
              coinGeckoId: 'crypto-com-chain',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'cro',
              coinMinimalDenom: 'basecro',
              coinDecimals: 8,
              coinGeckoId: 'crypto-com-chain',
            },
          ],
          gasPriceStep: {
            low: 0.025,
            average: 0.03,
            high: 0.04,
          },
          features: ['stargate', 'ibc-transfer'],
        },
        testnet4: {
          rpc: 'https://testnet-croeseid-4.crypto.org:26657',
          rpcConfig: undefined,
          rest: 'https://testnet-croeseid-4.crypto.org:1317',
          restConfig: undefined,
          chainId: 'testnet-croeseid-4',
          chainName: 'Crypto.org Testnet Croeseid-4',
          stakeCurrency: {
            coinDenom: 'tcro',
            coinMinimalDenom: 'basetcro',
            coinDecimals: 8,
            coinGeckoId: 'crypto-com-chain',
          },
          bip44: {
            // coinType: 394,
            coinType: 1,
          },
          bech32Config: Bech32Address.defaultBech32Config('tcro'),
          currencies: [
            {
              coinDenom: 'tcro',
              coinMinimalDenom: 'basetcro',
              coinDecimals: 8,
              coinGeckoId: 'crypto-com-chain',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'tcro',
              coinMinimalDenom: 'basetcro',
              coinDecimals: 8,
              coinGeckoId: 'crypto-com-chain',
            },
          ],
          gasPriceStep: {
            low: 0.025,
            average: 0.03,
            high: 0.04,
          },
          features: ['stargate', 'ibc-transfer'],
        },
      },
    },
    terra: {
      chainInfo: {
        mainnet: {
          bridgeRpcs: ['https://terra-rpc.crypto.org'],
          rpc: 'https://rpc-columbus.keplr.app',
          rest: 'https://lcd-columbus.keplr.app',
          chainId: 'columbus-5',
          chainName: 'Terra',
          stakeCurrency: {
            coinDenom: 'LUNA',
            coinMinimalDenom: 'uluna',
            coinDecimals: 6,
            coinGeckoId: 'terra-luna',
          },
          walletUrl: 'https://wallet.keplr.app/#/terra/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/terra/stake',
          bip44: {
            coinType: 330,
          },
          bech32Config: Bech32Address.defaultBech32Config('terra'),
          currencies: [
            {
              coinDenom: 'LUNA',
              coinMinimalDenom: 'uluna',
              coinDecimals: 6,
              coinGeckoId: 'terra-luna',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'LUNA',
              coinMinimalDenom: 'uluna',
              coinDecimals: 6,
              coinGeckoId: 'terra-luna',
            },
          ],
          coinType: 330,
          features: ['stargate', 'ibc-transfer'],
        },
        testnet: {
          rpc: 'https://rpc-columbus.keplr.app',
          rest: 'https://lcd-columbus.keplr.app',
          chainId: 'columbus-5',
          chainName: 'Terra',
          stakeCurrency: {
            coinDenom: 'LUNA',
            coinMinimalDenom: 'uluna',
            coinDecimals: 6,
            coinGeckoId: 'terra-luna',
          },
          walletUrl: 'https://wallet.keplr.app/#/terra/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/terra/stake',
          bip44: {
            coinType: 330,
          },
          bech32Config: Bech32Address.defaultBech32Config('terra'),
          currencies: [
            {
              coinDenom: 'LUNA',
              coinMinimalDenom: 'uluna',
              coinDecimals: 6,
              coinGeckoId: 'terra-luna',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'LUNA',
              coinMinimalDenom: 'uluna',
              coinDecimals: 6,
              coinGeckoId: 'terra-luna',
            },
          ],
          coinType: 330,
          features: ['stargate', 'ibc-transfer'],
        },
      },
    },
    juno: {
      chainInfo: {
        mainnet: {
          bridgeRpcs: ['https://juno-rpc.publicnode.com:443'],
          rpc: 'https://juno-rpc.publicnode.com:443',
          rest: 'https://lcd-juno.keplr.app',
          chainId: 'juno-1',
          chainName: 'Juno',
          stakeCurrency: {
            coinDenom: 'JUNO',
            coinMinimalDenom: 'ujuno',
            coinDecimals: 6,
            coinGeckoId: 'juno-network',
            coinImageUrl: 'https://dhj8dql1kzq2v.cloudfront.net/white/juno.png',
          },
          walletUrl: 'https://wallet.keplr.app/#/juno/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/juno/stake',
          bip44: {
            coinType: 118,
          },
          bech32Config: Bech32Address.defaultBech32Config('juno'),
          currencies: [
            {
              coinDenom: 'JUNO',
              coinMinimalDenom: 'ujuno',
              coinDecimals: 6,
              coinGeckoId: 'juno-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/juno.png',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'JUNO',
              coinMinimalDenom: 'ujuno',
              coinDecimals: 6,
              coinGeckoId: 'juno-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/juno.png',
            },
          ],
          coinType: 118,
          features: ['stargate', 'ibc-transfer'],
        },
        testnet: {
          bridgeRpcs: ['https://rpc-juno.keplr.app'],
          rpc: 'https://rpc-juno.keplr.app',
          rest: 'https://lcd-juno.keplr.app',
          chainId: 'juno-1',
          chainName: 'Juno',
          stakeCurrency: {
            coinDenom: 'JUNO',
            coinMinimalDenom: 'ujuno',
            coinDecimals: 6,
            coinGeckoId: 'juno-network',
            coinImageUrl: 'https://dhj8dql1kzq2v.cloudfront.net/white/juno.png',
          },
          walletUrl: 'https://wallet.keplr.app/#/juno/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/juno/stake',
          bip44: {
            coinType: 118,
          },
          bech32Config: Bech32Address.defaultBech32Config('juno'),
          currencies: [
            {
              coinDenom: 'JUNO',
              coinMinimalDenom: 'ujuno',
              coinDecimals: 6,
              coinGeckoId: 'juno-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/juno.png',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'JUNO',
              coinMinimalDenom: 'ujuno',
              coinDecimals: 6,
              coinGeckoId: 'juno-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/juno.png',
            },
          ],
          coinType: 118,
          features: ['stargate', 'ibc-transfer'],
        },
      },
    },
    // https://github.com/chainapsis/keplr-wallet/blob/master/packages/mobile/src/config.ts#L166
    akash: {
      chainInfo: {
        mainnet: {
          bridgeRpcs: ['https://rpc.akash.forbole.com:443'],
          rpc: 'https://rpc.akash.forbole.com:443',
          rest: 'https://lcd-akash.keplr.app',
          chainId: 'akashnet-2',
          chainName: 'Akash',
          stakeCurrency: {
            coinDenom: 'AKT',
            coinMinimalDenom: 'uakt',
            coinDecimals: 6,
            coinGeckoId: 'akash-network',
            coinImageUrl:
              'https://dhj8dql1kzq2v.cloudfront.net/white/akash.png',
          },
          walletUrl: 'https://wallet.keplr.app/#/akash/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/akash/stake',
          bip44: {
            coinType: 118,
          },
          bech32Config: Bech32Address.defaultBech32Config('akash'),
          currencies: [
            {
              coinDenom: 'AKASH',
              coinMinimalDenom: 'uakt',
              coinDecimals: 6,
              coinGeckoId: 'akash-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/akash.png',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'AKASH',
              coinMinimalDenom: 'uakt',
              coinDecimals: 6,
              coinGeckoId: 'akash-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/akash.png',
            },
          ],
          coinType: 118,
          features: ['stargate', 'ibc-transfer'],
        },
        testnet: {
          bridgeRpcs: ['https://rpc-akash.keplr.app'],
          rpc: 'https://rpc-akash.keplr.app',
          rest: 'https://lcd-akash.keplr.app',
          chainId: 'akashnet-2',
          chainName: 'Akash',
          stakeCurrency: {
            coinDenom: 'AKT',
            coinMinimalDenom: 'uakt',
            coinDecimals: 6,
            coinGeckoId: 'akash-network',
            coinImageUrl:
              'https://dhj8dql1kzq2v.cloudfront.net/white/akash.png',
          },
          walletUrl: 'https://wallet.keplr.app/#/akash/stake',
          walletUrlForStaking: 'https://wallet.keplr.app/#/akash/stake',
          bip44: {
            coinType: 118,
          },
          bech32Config: Bech32Address.defaultBech32Config('akash'),
          currencies: [
            {
              coinDenom: 'AKASH',
              coinMinimalDenom: 'uakt',
              coinDecimals: 6,
              coinGeckoId: 'akash-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/akash.png',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'AKASH',
              coinMinimalDenom: 'uakt',
              coinDecimals: 6,
              coinGeckoId: 'akash-network',
              coinImageUrl:
                'https://dhj8dql1kzq2v.cloudfront.net/white/akash.png',
            },
          ],
          coinType: 118,
          features: ['stargate', 'ibc-transfer'],
        },
      },
    },
    // https://docs.leapwallet.io/cosmos/for-dapps-connect-to-leap/suggest-chain-add-leap-to-a-non-native-chain
    canto: {
      chainInfo: {
        mainnet: {
          bridgeRpcs: ['https://rpc.canto.nodestake.top/'],
          rpc: 'https://rpc.canto.nodestake.top/',
          rest: 'https://rest.cosmos.directory/canto',
          chainId: 'canto_7700-1',
          chainName: 'canto',
          stakeCurrency: {
            coinDenom: 'canto',
            coinMinimalDenom: 'acanto',
            coinDecimals: 18,
            coinGeckoId: 'canto',
          },
          walletUrl: 'https://wallet.keplr.app/chains/canto',
          walletUrlForStaking: 'https://wallet.keplr.app/chains/canto',
          bip44: {
            coinType: 60,
          },
          bech32Config: {
            bech32PrefixAccAddr: 'canto',
            bech32PrefixAccPub: 'canto' + 'pub',
            bech32PrefixValAddr: 'canto' + 'valoper',
            bech32PrefixValPub: 'canto' + 'valoperpub',
            bech32PrefixConsAddr: 'canto' + 'valcons',
            bech32PrefixConsPub: 'canto' + 'valconspub',
          },
          currencies: [
            {
              coinDenom: 'canto',
              coinMinimalDenom: 'acanto',
              coinDecimals: 18,
              coinGeckoId: 'canto',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'canto',
              coinMinimalDenom: 'acanto',
              coinDecimals: 18,
              coinGeckoId: 'canto',
              gasPriceStep: {
                low: 0.01,
                average: 0.025,
                high: 0.04,
              },
            },
          ],
          features: ['ibc-transfer', 'ibc-go'],
        },
        testnet: {
          bridgeRpcs: ['https://rpc.canto.nodestake.top/'],
          rpc: 'https://rpc.canto.nodestake.top/',
          rest: 'https://api.canto.nodestake.top',
          chainId: 'canto_7700-1',
          chainName: 'canto',
          stakeCurrency: {
            coinDenom: 'canto',
            coinMinimalDenom: 'acanto',
            coinDecimals: 18,
            coinGeckoId: 'canto',
          },
          walletUrl: 'https://wallet.keplr.app/chains/canto',
          walletUrlForStaking: 'https://wallet.keplr.app/chains/canto',
          bip44: {
            coinType: 60,
          },
          bech32Config: {
            bech32PrefixAccAddr: 'canto',
            bech32PrefixAccPub: 'canto' + 'pub',
            bech32PrefixValAddr: 'canto' + 'valoper',
            bech32PrefixValPub: 'canto' + 'valoperpub',
            bech32PrefixConsAddr: 'canto' + 'valcons',
            bech32PrefixConsPub: 'canto' + 'valconspub',
          },
          currencies: [
            {
              coinDenom: 'canto',
              coinMinimalDenom: 'acanto',
              coinDecimals: 18,
              coinGeckoId: 'canto',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'canto',
              coinMinimalDenom: 'acanto',
              coinDecimals: 18,
              coinGeckoId: 'canto',
              gasPriceStep: {
                low: 0.01,
                average: 0.025,
                high: 0.04,
              },
            },
          ],
          features: ['ibc-transfer', 'ibc-go'],
        },
      },
    },
    // https://github.com/chainapsis/keplr-wallet/blob/468a67515e77b1a2345f65d5b1e9eb1d9c5000cc/packages/extension/src/config.ts#L848-L905
    iris: {
      chainInfo: {
        mainnet: {
          bridgeRpcs: ['https://rpc-iris.keplr.app'],
          rpc: 'https://rpc-iris.keplr.app',
          rest: 'https://lcd-iris.keplr.app',
          chainId: 'irishub-1',
          chainName: 'IRISnet',
          stakeCurrency: {
            coinDenom: 'IRIS',
            coinMinimalDenom: 'uiris',
            coinDecimals: 6,
            coinGeckoId: 'iris-network',
          },
          walletUrl: 'https://wallet.keplr.app/chains/irisnet',
          walletUrlForStaking: 'https://wallet.keplr.app/chains/irisnet',
          bip44: {
            coinType: 118,
          },
          alternativeBIP44s: [
            {
              coinType: 566,
            },
          ],
          bech32Config: {
            bech32PrefixAccAddr: 'iaa',
            bech32PrefixAccPub: 'iap',
            bech32PrefixValAddr: 'iva',
            bech32PrefixValPub: 'ivp',
            bech32PrefixConsAddr: 'ica',
            bech32PrefixConsPub: 'icp',
          },
          currencies: [
            {
              coinDenom: 'IRIS',
              coinMinimalDenom: 'uiris',
              coinDecimals: 6,
              coinGeckoId: 'iris-network',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'IRIS',
              coinMinimalDenom: 'uiris',
              coinDecimals: 6,
              coinGeckoId: 'iris-network',
              gasPriceStep: {
                low: 0.2,
                average: 0.3,
                high: 0.4,
              },
            },
          ],
          features: ['ibc-transfer', 'ibc-go'],
        },
        testnet: {
          bridgeRpcs: ['https://rpc.akash.forbole.com:443'],
          rpc: 'https://rpc-iris.keplr.app',
          rest: 'https://lcd-iris.keplr.app',
          chainId: 'irishub-1',
          chainName: 'IRISnet',
          stakeCurrency: {
            coinDenom: 'IRIS',
            coinMinimalDenom: 'uiris',
            coinDecimals: 6,
            coinGeckoId: 'iris-network',
          },
          walletUrl: 'https://wallet.keplr.app/chains/irisnet',
          walletUrlForStaking: 'https://wallet.keplr.app/chains/irisnet',
          bip44: {
            coinType: 118,
          },
          alternativeBIP44s: [
            {
              coinType: 566,
            },
          ],
          bech32Config: {
            bech32PrefixAccAddr: 'iaa',
            bech32PrefixAccPub: 'iap',
            bech32PrefixValAddr: 'iva',
            bech32PrefixValPub: 'ivp',
            bech32PrefixConsAddr: 'ica',
            bech32PrefixConsPub: 'icp',
          },
          currencies: [
            {
              coinDenom: 'IRIS',
              coinMinimalDenom: 'uiris',
              coinDecimals: 6,
              coinGeckoId: 'iris-network',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'IRIS',
              coinMinimalDenom: 'uiris',
              coinDecimals: 6,
              coinGeckoId: 'iris-network',
              gasPriceStep: {
                low: 0.2,
                average: 0.3,
                high: 0.4,
              },
            },
          ],
          features: ['ibc-transfer', 'ibc-go'],
        },
      },
    },
    // https://github.com/chainapsis/keplr-wallet/blob/1e45bc0fc40dfa3a9eaf891fd229fd4ba01be6c5/packages/extension/src/config.ts#L2326
    celestia: {
      chainInfo: {
        mainnet: {
          bridgeRpcs: [],
          rpc: 'https://celestia-rpc.publicnode.com:443',
          rest: 'https://lcd-celestia.keplr.app',
          chainId: 'celestia',
          chainName: 'Celestia',
          stakeCurrency: {
            coinDenom: 'TIA',
            coinMinimalDenom: 'utia',
            coinDecimals: 6,
          },
          walletUrl: 'https://wallet.keplr.app/chains/celestia',
          walletUrlForStaking: 'https://wallet.keplr.app/chains/celestia',
          bip44: {
            coinType: 118,
          },
          bech32Config: Bech32Address.defaultBech32Config('celestia'),
          currencies: [
            {
              coinDenom: 'TIA',
              coinDecimals: 6,
              coinMinimalDenom: 'utia',
            },
          ],
          feeCurrencies: [
            {
              coinDenom: 'TIA',
              coinDecimals: 6,
              coinMinimalDenom: 'utia',
            },
          ],
          features: [],
        },
      },
    },
  },
  cronos: {
    gasLimit: {
      warp: 500_000,
      pool: 50_000,
    },
    chainInfo: {
      ethereum: {
        chainId: '0x01',
        chainName: 'Ethereum Mainnet',
        nativeCurrency: {
          name: 'ETH',
          symbol: 'ETH',
          decimals: 18,
        },
        rpcUrls: [
          'https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
        ],
        blockExplorerUrls: ['https://etherscan.io/'],
      },
      kovan: {
        chainId: '0x2A',
        chainName: 'Ethereum Testnet Kovan',
        nativeCurrency: {
          name: 'ETH',
          symbol: 'ETH',
          decimals: 18,
        },
        rpcUrls: [
          'https://kovan.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
        ],
        blockExplorerUrls: ['https://kovan.etherscan.io/'],
      },
      goerli: {
        chainId: '0x5',
        chainName: 'Ethereum Testnet Goerli',
        nativeCurrency: {
          name: 'ETH',
          symbol: 'ETH',
          decimals: 18,
        },
        rpcUrls: [
          'https://goerli.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
        ],
        blockExplorerUrls: ['https://goerli.etherscan.io/'],
      },
      mainnet: {
        chainId: '0x19',
        chainName: 'Cronos',
        nativeCurrency: {
          name: 'CRO',
          symbol: 'CRO',
          decimals: 18,
        },
        rpcUrls: ['https://evm-cronos.crypto.org'],
        blockExplorerUrls: ['https://cronos.org/explorer'],
      },
      testnet: {
        chainId: '0x152',
        chainName: 'Cronos Testnet',
        nativeCurrency: {
          name: 'tCRO',
          symbol: 'tCRO',
          decimals: 18,
        },
        rpcUrls: ['https://evm-p11.cronos.org'],
        blockExplorerUrls: ['https://cronos.org/explorer/pioneer11'],
      },
      gravityTestnet: {
        chainId: '0x154',
        chainName: 'Cronos Testnet',
        nativeCurrency: {
          name: 'tCRO',
          symbol: 'tCRO',
          decimals: 18,
        },
        rpcUrls: ['https://evm-p11.cronos.org'],
        blockExplorerUrls: ['https://cronos.org/explorer/pioneer11'],
      },
    },
    extra: {
      mainnet: {
        contractAddresses: {
          gravity: '0x8eD0209b5e3B9B323900292ad692d0f71711b339', // not up-to-date
          ethGravity: '0x11653113e631C2F7CeaBbae2Ff34D7e73369d379', // not up-to-date
          croBridge: '0x6b1b50c2223eb31E0d4683b046ea9C6CB0D0ea4F',
        },
        crc21ContractUrl: 'https://cronos-dryrun.3ona.co:1317',
      },
      testnet: {
        contractAddresses: {
          gravity: '0x0c7F574e2e249408C73eFCC23dBe70049E2e27eA', // deployed on Kovan
          ethGravity: '0x2DF69b3A6C1F82d056b5b187E6A2C05dBe651b9e', // deployed on Kovan
          croBridge: '0xCE13a6F3d4167CE958f4764D423e6D62a114c751', // updated as of 4 Jan 2022
        },
        crc21ContractUrl: 'https://rest-gt2.cronos.org',
      },
      gravityTestnet: {
        contractAddresses: {
          gravity: '0xAA1Ca02916a7C30d827226450540b6240E4a93CF', // deployed on Goerli
          ethGravity: '0xf9D339270A6369776dE9eA6F57222ea01c368D2F', // deployed on Goerli
          croBridge: '0x38F05eb0c209c4c9Fe2D6E237f03ec503f65F088', // updated as of 27 Jun 2022
        },
        crc21ContractUrl: 'https://rest-p11.cronos.org',
      },
    },
  },
  ibc: {
    channelInfo: {
      cryptoOrgToCronos: {
        mainnet: {
          channelId: 44,
          sourceChannel: 'channel-44',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 131,
          sourceChannel: 'channel-131',
          sourcePort: 'transfer',
        },
      },
      cosmosHubToCronos: {
        mainnet: {
          channelId: 241,
          sourceChannel: 'channel-241',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 131,
          sourceChannel: 'channel-131',
          sourcePort: 'transfer',
        },
      },
      terraToCronos: {
        mainnet: {
          channelId: 22,
          sourceChannel: 'channel-22',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 131,
          sourceChannel: 'channel-131',
          sourcePort: 'transfer',
        },
      },
      junoToCronos: {
        mainnet: {
          channelId: 454,
          sourceChannel: 'channel-454',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 454,
          sourceChannel: 'channel-454',
          sourcePort: 'transfer',
        },
      },
      akashToCronos: {
        mainnet: {
          channelId: 40,
          sourceChannel: 'channel-40',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 40,
          sourceChannel: 'channel-40',
          sourcePort: 'transfer',
        },
      },
      cantoToCronos: {
        mainnet: {
          channelId: 22,
          sourceChannel: 'channel-22',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 22,
          sourceChannel: 'channel-22',
          sourcePort: 'transfer',
        },
      },
      irisToCronos: {
        mainnet: {
          channelId: 67,
          sourceChannel: 'channel-67',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 67,
          sourceChannel: 'channel-67',
          sourcePort: 'transfer',
        },
      },
      celestiaToCronos: {
        mainnet: {
          channelId: 16,
          sourceChannel: 'channel-16',
          sourcePort: 'transfer',
        },
        testnet: {
          channelId: 16,
          sourceChannel: 'channel-16',
          sourcePort: 'transfer',
        },
      },
    },
  },
  txsApi: {
    mainnet: {
      baseUrl: 'https://cronos.org/indexing/api/v1/bridges',
    },
    testnet: {
      baseUrl: 'https://cronos.org/testnet3/indexing/api/v1/bridges',
    },
  },
  ethereumSupportedChainIds: {
    mainnet: {
      ethereum: [EthChainId.Mainnet],
      cronos: [EthChainId.Cronos],
      defiWalletEth: [EthChainId.Mainnet],
      defiWalletCronos: [EthChainId.Cronos],
    },
    testnet: {
      ethereum: [EthChainId.Goerli],
      cronos: [EthChainId.CronosGravityTestnet],
      defiWalletEth: [EthChainId.Goerli],
      defiWalletCronos: [EthChainId.CronosGravityTestnet],
    },
  },
  walletSupportedNetworks: {
    testnet: {
      [WalletType.deficonnect]: [
        Network.Ethereum,
        Network.Cronos,
        Network.CryptoOrg,
      ],
      [WalletType.keplr]: [Network.CryptoOrg, Network.Cosmos],
      [WalletType.metamask]: [Network.Ethereum, Network.Cronos],
    },
    mainnet: {
      [WalletType.deficonnect]: [
        Network.Cronos,
        Network.CryptoOrg,
        Network.Cosmos,
      ],
      [WalletType.keplr]: [
        Network.CryptoOrg,
        Network.Cosmos,
        Network.Terra,
        Network.Juno,
        Network.Akash,
        Network.Canto,
        Network.Iris,
        Network.Celestia,
      ],
      [WalletType.metamask]: [Network.Cronos],
    },
  },
  networks: {
    testnet: {
      [Network.Ethereum]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: false,
          },
        },
      },
      [Network.CryptoOrg]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },
      [Network.Cronos]: {
        disabled: false,
        toNetworks: {
          [Network.CryptoOrg]: {
            disabled: false,
            addressInput: true,
          },
          [Network.Ethereum]: {
            disabled: false,
            addressInput: false,
          },
        },
      },
    } as Networks,
    mainnet: {
      [Network.Ethereum]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: false,
          },
        },
      },
      [Network.CryptoOrg]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },
      [Network.Cosmos]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },
      /*[Network.Terra]: {
        disabled: true,
        toNetworks: {
          [Network.Cronos]: {
            disabled: true,
            addressInput: true,
          },
        },
      },*/
      /*[Network.Juno]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },*/
      [Network.Akash]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },
      [Network.Canto]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },
      [Network.Iris]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },
      [Network.Celestia]: {
        disabled: false,
        toNetworks: {
          [Network.Cronos]: {
            disabled: false,
            addressInput: true,
          },
        },
      },
      [Network.Cronos]: {
        disabled: false,
        toNetworks: {
          [Network.CryptoOrg]: {
            disabled: false,
            addressInput: true,
          },
          [Network.Cosmos]: {
            disabled: false,
            addressInput: true,
          },
          /*[Network.Terra]: {
            disabled: true,
            addressInput: true,
          },*/
          /*[Network.Juno]: {
            disabled: false,
            addressInput: true,
          },*/
          [Network.Akash]: {
            disabled: false,
            addressInput: true,
          },
          [Network.Canto]: {
            disabled: false,
            addressInput: true,
          },
          [Network.Iris]: {
            disabled: false,
            addressInput: true,
          },
          [Network.Celestia]: {
            disabled: false,
            addressInput: true,
          },
          [Network.Ethereum]: {
            disabled: false,
            addressInput: false,
          },
        },
      },
    } as Networks,
  },
  defiWallet: {
    networkUrls: {
      [EthChainId.Mainnet]: `https://mainnet.infura.io/v3/${infuraKey}`,
      [EthChainId.Rinkeby]: `https://rinkeby.infura.io/v3/${infuraKey}`,
      [EthChainId.Ropsten]: `https://ropsten.infura.io/v3/${infuraKey}`,
      [EthChainId.Goerli]: `https://goerli.infura.io/v3/${infuraKey}`,
      [EthChainId.Kovan]: `https://kovan.infura.io/v3/${infuraKey}`,
      [EthChainId.Cronos]: 'https://evm-cronos.crypto.org/',
      [EthChainId.CronosTestnet]: 'https://cronos-testnet-3.crypto.org:8545/',
    },
  },
  explorers: {
    [Network.CryptoOrg]: {
      [CroChain.mainnet]: 'https://cronos-pos.org/explorer/tx/',
      [CroChain.testnet4]: 'https://cronos-pos.org/explorer/croeseid4/tx/',
    },
    [Network.Cronos]: {
      [CronosChain.mainnet]: 'https://cronoscan.com/tx/',
      [CronosChain.testnet]: 'https://cronos.org/explorer/testnet3/tx/',
      [CronosChain.gravityTestnet]: 'https://cronos.org/explorer/pioneer11/tx/',
    },
    [Network.Cosmos]: {
      [CosmosHubChain.mainnet]: 'https://www.mintscan.io/cosmos/tx/',
      // [CosmosHubChain.testnet]: 'https://www.mintscan.io/cosmos/txs/',
      // [CosmosHubChain.testnet]: 'https://www.mintscan.io/cosmos/txs/',
    },
    [Network.Terra]: {
      [TerraChain.mainnet]: 'https://terra.stake.id/?#/tx/',
    },
    [Network.Juno]: {
      [JunoChain.mainnet]: 'https://www.mintscan.io/juno/tx/',
    },
    [Network.Akash]: {
      [AkashChain.mainnet]: 'https://www.mintscan.io/akash/tx/',
    },
    [Network.Canto]: {
      [CantoChain.mainnet]: 'https://www.mintscan.io/canto/tx/',
    },
    [Network.Iris]: {
      [IrisChain.mainnet]: 'https://www.mintscan.io/iris/tx/',
    },
    [Network.Celestia]: {
      [CelestiaChain.mainnet]: 'https://www.mintscan.io/celestia/tx/',
    },
    [Network.Ethereum]: {
      [EthChainId.Mainnet]: 'https://etherscan.io/tx/',
      [EthChainId.Ropsten]: 'https://ropsten.etherscan.io/tx/',
      [EthChainId.Rinkeby]: 'https://rinkeby.etherscan.io/tx/',
      [EthChainId.Goerli]: 'https://goerli.etherscan.io/tx/',
      [EthChainId.Kovan]: 'https://kovan.etherscan.io/tx/',
      [EthChainId.Cronos]: 'https://cronoscan.com/tx',
      [EthChainId.CronosTestnet]: 'https://cronos.org/explorer/testnet3/tx/',
      [EthChainId.CronosGravityTestnet]:
        'https://cronos.org/explorer/pioneer11/tx',
    },
  } as NetworkChainExplorer,
  walletDownloadUrls: {
    [WalletType.metamask]: {
      [UserAgent.ios]: process.env.NEXT_PUBLIC_METAMASK_DEEP_LINK_URL,
      [UserAgent.android]: process.env.NEXT_PUBLIC_METAMASK_DEEP_LINK_URL,
      [UserAgent.desktop]:
        'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn?hl=en',
    },
    [WalletType.keplr]: {
      [UserAgent.ios]:
        'https://apps.apple.com/hk/app/keplr-wallet/id1567851089?l=en',
      [UserAgent.android]:
        'https://play.google.com/store/apps/details?id=com.chainapsis.keplr',
      [UserAgent.desktop]:
        'https://chrome.google.com/webstore/detail/keplr/dmkamcknogkgcdfhhbddcghachkejeap?hl=en',
    },
  } as {
    [key in WalletType]?: {
      [key in UserAgent]: string;
    };
  },
};

export default baseStaticConfig;
